import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Upload, Loader2, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCoupons = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState({});
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');

  const handleDeleteCoupon = async (couponId, couponCode) => {
    if (!window.confirm(`Are you sure you want to delete coupon "${couponCode}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`);
      setUploadMessage(`Successfully deleted coupon ${couponCode}`);
      fetchCoupons();
      setTimeout(() => setUploadMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting coupon:', error);
      setUploadMessage('Failed to delete coupon. It may have been used in an order.');
    }
  };

  useEffect(() => {
    const isAuth = localStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    fetchCoupons();
  }, [navigate]);

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/admin/coupons`);
      setInventory(response.data.inventory);
      setCoupons(response.data.coupons);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setUploadMessage('Please upload a CSV file');
      return;
    }

    setUploading(true);
    setUploadMessage('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/coupons/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadMessage(`Successfully uploaded ${response.data.uploaded} coupon codes`);
      fetchCoupons();
    } catch (error) {
      console.error('Error uploading coupons:', error);
      setUploadMessage('Failed to upload coupons. Please check the CSV format.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadSampleCSV = () => {
    const csvContent = 'Code,Subscription Plan Title\nQUARTERLY-SAMPLE-001,Individual Quarterly Plan\nANNUAL-SAMPLE-001,Individual Annual Plan';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_coupons.csv';
    a.click();
  };

  return (
    <AdminLayout>
      <div>
        <h1 className="text-3xl font-bold text-black mb-8">Coupon Management</h1>

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-black mb-4">Upload Coupons</h2>
          <p className="text-gray-600 mb-4">
            Upload a CSV file with columns: <code className="bg-gray-100 px-2 py-1 rounded">Code</code> and <code className="bg-gray-100 px-2 py-1 rounded">Subscription Plan Title</code>
          </p>
          
          <div className="flex flex-wrap gap-4 items-center mb-4">
            <label className="px-6 py-3 bg-[#1E88E5] text-white rounded-lg hover:bg-[#1565C0] transition-colors cursor-pointer flex items-center space-x-2">
              <Upload className="w-5 h-5" />
              <span>{uploading ? 'Uploading...' : 'Choose CSV File'}</span>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
                data-testid="upload-csv-input"
              />
            </label>
            
            <button
              onClick={downloadSampleCSV}
              className="px-6 py-3 border-2 border-[#1E88E5] text-[#1E88E5] rounded-lg hover:bg-[#E3F2FD] transition-colors flex items-center space-x-2"
            >
              <Download className="w-5 h-5" />
              <span>Download Sample CSV</span>
            </button>
          </div>

          {uploadMessage && (
            <div className={`p-4 rounded-lg ${
              uploadMessage.includes('Success') 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {uploadMessage}
            </div>
          )}
        </div>

        {/* Inventory Overview */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-black mb-4">Inventory Overview</h2>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 text-[#1E88E5] animate-spin" />
            </div>
          ) : Object.keys(inventory).length === 0 ? (
            <p className="text-gray-600 text-center py-8">No coupons uploaded yet</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(inventory).map(([plan, stats]) => (
                <div key={plan} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-black mb-3">{plan}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total:</span>
                      <span className="font-semibold">{stats.total}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-600">Available:</span>
                      <span className="font-semibold text-green-600">{stats.available}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-yellow-600">Assigned:</span>
                      <span className="font-semibold text-yellow-600">{stats.assigned}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-600">Used:</span>
                      <span className="font-semibold text-red-600">{stats.used}</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${(stats.available / stats.total) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {Math.round((stats.available / stats.total) * 100)}% available
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* All Coupons Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-black mb-4">All Coupons</h2>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 text-[#1E88E5] animate-spin" />
            </div>
          ) : coupons.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No coupons available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Code</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Plan</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Uploaded</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.slice(0, 50).map((coupon) => (
                    <tr key={coupon.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm">{coupon.code}</td>
                      <td className="py-3 px-4 text-sm">{coupon.subscription_plan}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          coupon.status === 'available' 
                            ? 'bg-green-100 text-green-700' 
                            : coupon.status === 'assigned'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {coupon.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {new Date(coupon.uploaded_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        {coupon.status === 'available' && (
                          <button
                            onClick={() => handleDeleteCoupon(coupon.id, coupon.code)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center space-x-1"
                            data-testid={`delete-coupon-${coupon.code}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Delete</span>
                          </button>
                        )}
                        {coupon.status !== 'available' && (
                          <span className="text-gray-400 text-sm italic">Used/Assigned</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {coupons.length > 50 && (
                <p className="text-sm text-gray-600 text-center py-4">
                  Showing first 50 of {coupons.length} coupons
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCoupons;